import { computed, defineComponent, ref, toRef, watch, } from '@vue/composition-api';
import { QuestionType, SURVEY_ANSWER_SEPARATOR, } from '@/api/v2/requests/surveys';
import { TextInput } from '@/components/Inputs/TextInput';
import { RadioGroup } from '@/components/Inputs/Radio';
import { CheckboxGroup } from '@/components/Inputs';
import { FileDropZone } from '@/components/FileDropZone';
import { confirmFileUpload, createFileUpload, uploadFileToS3, normalizeFileName, FileVisibility, } from '@/api/v2/requests/files';
import { apiV2Url as v2ApiUrl } from '@/api/v2';
import DeleteSvg from '@/assets/icons/delete.svg';
import { Icon } from '@/components/Icon';
import Vue from 'vue';
import ImageModal from '@/components/Modal/ImageModal.vue';
import { getSurveyImageUrls } from './survey-image-urls';
export default defineComponent({
    name: 'SurveyQuestion',
    props: {
        surveyId: { type: String, required: true },
        idx: { type: Number, required: true },
        question: {
            type: Object,
            required: true,
        },
        hasError: {
            type: Boolean,
            default: false,
        },
        fileHasError: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        allowBranchingPreview: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, ctx) {
        const id = computed(() => `q-${props.idx}`);
        const questionDomId = computed(() => `survey-question-${props.idx}`);
        const files = ref(props.question.value && props.question.type === QuestionType.FILE
            ? props.question.value.split(SURVEY_ANSWER_SEPARATOR).filter(Boolean)
            : []);
        const imageUrls = computed(() => getSurveyImageUrls(props.question.fileUrl));
        const fullQuestionImageUrl = computed(() => imageUrls.value.fullUrl);
        const thumbnailQuestionImageUrl = computed(() => imageUrls.value.thumbnailUrl);
        watch(files, (newFiles) => {
            if (!props.readonly) {
                props.question.value = newFiles.join(SURVEY_ANSWER_SEPARATOR);
            }
        }, { deep: true });
        const attachmentFile = ref(null);
        const attachmentFileProp = toRef(props.question, 'attachmentFile');
        watch(attachmentFileProp, (newValue) => {
            if (newValue !== undefined && newValue !== attachmentFile.value) {
                attachmentFile.value = newValue;
            }
        }, { immediate: true });
        watch(attachmentFile, (newFile) => {
            if (!props.readonly) {
                Vue.set(props.question, 'attachmentFile', newFile);
            }
        });
        const commentText = ref('');
        const commentProp = toRef(props.question, 'comment');
        watch(commentProp, (newValue) => {
            if (newValue !== undefined && newValue !== commentText.value) {
                commentText.value = newValue || '';
            }
        }, { immediate: true });
        watch(commentText, (newComment) => {
            if (!props.readonly) {
                Vue.set(props.question, 'comment', newComment);
            }
        });
        const handleQuestionAttachment = async (event) => {
            if (props.readonly)
                return;
            const eventFiles = Array.from(event.target.files);
            if (eventFiles.length > 0 && props.question.isQuestionWithFile) {
                const [file] = eventFiles;
                const url = await uploadFile(file);
                attachmentFile.value = url;
            }
        };
        const removeAttachmentFile = () => {
            if (props.readonly)
                return;
            attachmentFile.value = null;
        };
        const uploadFile = async (file) => {
            if (props.readonly)
                return '';
            ctx.emit('update:is-loading', { id, value: true });
            const fileUploadHandle = await createFileUpload({
                fileName: normalizeFileName(file.name),
                fileSize: file.size,
                visibility: FileVisibility.SurveyAnswer,
                guards: {
                    surveyId: props.surveyId,
                },
                shouldGenerateThumbnail: true,
            });
            await uploadFileToS3(file, fileUploadHandle);
            await confirmFileUpload(fileUploadHandle.fileId);
            ctx.emit('file-name-mapping', { [fileUploadHandle.fileId]: file.name });
            ctx.emit('update:is-loading', { id, value: false });
            return `${v2ApiUrl}/files/${fileUploadHandle.fileId}?ext=${fileUploadHandle.extension}`;
        };
        const setCheckboxValue = ($e) => {
            if (props.readonly && !props.allowBranchingPreview)
                return;
            const options = props.question.options;
            if (!options || options.length === 0)
                return;
            const checkedValues = Array.from($e).map(Boolean);
            checkedValues.forEach((checked, idx) => {
                options[idx].isSelected = checked;
            });
            const selectedLabels = options
                .filter((o, idx) => checkedValues[idx])
                .map((o) => o.label);
            props.question.value = selectedLabels.join(SURVEY_ANSWER_SEPARATOR);
        };
        const handleAddFiles = async (event) => {
            if (props.readonly)
                return;
            const eventFiles = Array.from(event.target.files);
            if (eventFiles.length > 0) {
                if (props.question.allowMultiple) {
                    const urls = await Promise.all(eventFiles.map(uploadFile));
                    files.value.push(...urls);
                }
                else {
                    const [file] = eventFiles;
                    const url = await uploadFile(file);
                    files.value.splice(0, files.value.length, url);
                }
            }
        };
        const removeFile = (file) => {
            if (props.readonly)
                return;
            files.value.splice(files.value.indexOf(file), 1);
        };
        const radioOptions = computed(() => props.question.options);
        const checkboxOptions = computed(() => props.question.options);
        return {
            id,
            questionDomId,
            files,
            fullQuestionImageUrl,
            thumbnailQuestionImageUrl,
            QuestionType,
            DeleteSvg,
            handleAddFiles,
            removeFile,
            setCheckboxValue,
            radioOptions,
            checkboxOptions,
            handleQuestionAttachment,
            removeAttachmentFile,
            attachmentFile,
            commentText,
        };
    },
    components: {
        TextInput,
        RadioGroup,
        CheckboxGroup,
        FileDropZone,
        Icon,
        ImageModal,
    },
});
